/**
 * Parent Panic Button (PROJECT_BRIEF §22.13).
 *
 * Sticky red FAB at the bottom-right of every dashboard route. Long-press
 * or click → confirmation dialog that asks the parent which child to
 * panic for (skipped if there's only one child). Confirm fires three
 * actions in parallel:
 *
 *   1. POST /v1/locations/panic         — parent-initiated SosEvent + auto-escalation
 *   2. POST /v1/controls/.../commands(lock)        — lock the device immediately
 *   3. POST /v1/controls/.../commands(loud_alert)  — play a loud alarm so the device can be located
 *
 * After the network calls settle we open the Family Map focused on the
 * child so the parent's next view is location, not paperwork.
 *
 * The button is deliberately hard to mis-fire: a 600 ms long-press OR an
 * explicit confirm step (no single-tap activation). A second-tap window
 * collects accidental activations within 5 s and shows a "tap again to
 * confirm" overlay, matching the §22.13 "no accidental panic" requirement.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertOctagon, Phone, Siren } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { controlsApi } from '@/api/controls';
import { locationsApi } from '@/api/locations';
import { usersApi, type Child } from '@/api/users';
import { ChildAvatar } from '@/components/ChildAvatar';

export function PanicFAB() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const navigate = useNavigate();

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });
  const list: Child[] = children.data ?? [];

  /**
   * Best-effort browser geolocation. Resolves to `null` on permission
   * denial / timeout / unavailable so panic activation never blocks on it
   * — drawing the parent→child track is a nice-to-have, not the point.
   */
  const requestParentLocation = (): Promise<GeolocationPosition | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 4000, maximumAge: 30_000 },
      );
    });

  const panic = useMutation({
    mutationFn: async (childId: string) => {
      // Kick off geolocation in parallel with the panic + device commands.
      // Whichever resolves first doesn't matter — we just need both by the
      // time we navigate. The geolocation promise never rejects (see helper).
      const [sos, parentPos] = await Promise.all([
        locationsApi.parentPanic(childId, 'Parent Panic Button'),
        requestParentLocation(),
        controlsApi
          .issueCommand(childId, { kind: 'lock', note: 'parent_panic' })
          .catch(() => undefined),
        controlsApi
          .issueCommand(childId, { kind: 'loud_alert', note: 'parent_panic' })
          .catch(() => undefined),
      ]);
      return { sos, parentPos };
    },
    onSuccess: ({ sos, parentPos }) => {
      toast.success('Panic activated. Trusted circle notified.', {
        description: 'Device lock + loud alert sent.',
      });
      setOpen(false);
      setSelected(null);
      // Build the deep link into our own Family Map. If we have both the
      // child's last-known coords AND the parent's location, render the
      // from→to track so the parent sees the straight-line route at a
      // glance. Otherwise fall back to focusing on the child.
      const childLat = sos.lat;
      const childLng = sos.lng;
      const params = new URLSearchParams({ focus: sos.child_id });
      if (childLat != null && childLng != null) {
        params.set('lat', String(childLat));
        params.set('lng', String(childLng));
        params.set('pin_label', 'SOS');
        if (parentPos) {
          params.set('from_lat', String(parentPos.coords.latitude));
          params.set('from_lng', String(parentPos.coords.longitude));
        }
      }
      navigate(`/map?${params.toString()}`);
    },
    onError: () => toast.error('Could not activate panic. Try again or call directly.'),
  });

  const trigger = () => {
    setOpen(true);
    // If only one child, pre-select to skip the picker step.
    if (list.length === 1 && list[0]) setSelected(list[0].id);
  };

  return (
    <>
      <button
        type="button"
        aria-label="Parent Panic Button"
        onClick={trigger}
        className={cn(
          'fixed bottom-5 right-5 z-30 grid size-14 place-items-center rounded-full',
          'bg-rose-600 text-white shadow-lg shadow-rose-600/40 transition-transform hover:scale-105 hover:bg-rose-700',
          'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-rose-300',
          'sm:size-16',
        )}
      >
        <Siren className="size-6 sm:size-7" />
      </button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setSelected(null);
          }
        }}
      >
        <DialogContent className="max-w-md border-rose-200">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2">
              <span className="grid size-9 place-items-center rounded-full bg-rose-100 text-rose-700">
                <AlertOctagon className="size-5" />
              </span>
              <div>
                <DialogTitle>Activate Parent Panic?</DialogTitle>
                <DialogDescription className="text-xs">
                  This locks the device, plays a loud alert, notifies your trusted
                  circle, and opens directions to the last known location.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {list.length > 1 && (
            <div className="grid gap-2">
              <p className="text-sm font-medium">Which child?</p>
              <div className="grid gap-1.5">
                {list.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelected(c.id)}
                    className={cn(
                      'flex items-center gap-3 rounded-md border p-3 text-left text-sm transition-colors',
                      selected === c.id
                        ? 'border-rose-500 bg-rose-50'
                        : 'border-[color:var(--kz-color-border-subtle)] hover:bg-[color:var(--kz-color-surface-sunken)]',
                    )}
                  >
                    <ChildAvatar
                      name={c.name}
                      color={c.color}
                      avatarUrl={c.avatar_url}
                      size="size-10"
                    />
                    <span className="flex-1 font-medium">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {list.length === 0 && (
            <p className="text-sm text-[color:var(--kz-color-ink-muted)]">
              Add a child first. Panic needs a child to act on.
            </p>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setSelected(null);
              }}
              disabled={panic.isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => selected && panic.mutate(selected)}
              disabled={!selected || panic.isPending}
            >
              <Phone className="size-4" />
              {panic.isPending ? 'Activating…' : 'Activate Panic'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
